import { useSearch } from "@tanstack/react-router";

import { KpiTile } from "@/components/cinch/KpiTile";
import { SectionDivider } from "@/components/cinch/SectionDivider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useTopOfFunnel, type TofFunnel } from "../api";
import { CroForecastFilterBar } from "../components/FilterBar";
import { LockedBanner } from "../components/LockedBanner";
import { NotesArea } from "../components/NotesArea";
import { PipelineBySourceCard } from "../components/PipelineBySourceCard";
import { CroForecastSubNav } from "../components/SubNav";
import { fmtCents, fmtCount, fmtDate, fmtMonthName, fmtPct, fmtQuarter } from "../format";
import { resolveSearch, type CroForecastSearch } from "../search";

export function CroForecastTopOfFunnelPage() {
  const search = useSearch({ from: "/cro-forecast/top-of-funnel" }) as CroForecastSearch;
  const { year, week } = resolveSearch(search);
  const { data, isLoading, error } = useTopOfFunnel(year, week);

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <LockedBanner
          status={data?.lock_status}
          isoYear={year}
          isoWeek={week}
        />
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {error && (
          <p className="text-sm text-destructive">
            Failed: {(error as Error).message}
          </p>
        )}
        {data && (
          <>
            {/* Funnel KPIs — three periods, four cards each */}
            <SectionDivider label={`Week ${week}`} description="This ISO week" />
            <FunnelRow funnel={data.funnel_week} />

            <SectionDivider
              label={`${fmtMonthName(data.fiscal_month)} ${year}`}
              description="Month-to-date"
              tone="accent"
            />
            <FunnelRow funnel={data.funnel_month} />

            <SectionDivider
              label={fmtQuarter(year, data.quarter)}
              description="Quarter-to-date"
              tone="accent"
            />
            <FunnelRow funnel={data.funnel_quarter} />

            {/* BDR by period */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  BDR — Per-rep pipeline added (week / month / quarter)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.bdr_team.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No BDR-attributed deals in the period.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead rowSpan={2}>BDR</TableHead>
                        <TableHead colSpan={2} className="text-center">
                          Week
                        </TableHead>
                        <TableHead colSpan={2} className="text-center">
                          Month
                        </TableHead>
                        <TableHead colSpan={2} className="text-center">
                          Quarter
                        </TableHead>
                      </TableRow>
                      <TableRow>
                        <TableHead className="text-right text-[10px]">
                          Deals
                        </TableHead>
                        <TableHead className="text-right text-[10px]">
                          Pipeline $
                        </TableHead>
                        <TableHead className="text-right text-[10px]">
                          Deals
                        </TableHead>
                        <TableHead className="text-right text-[10px]">
                          Pipeline $
                        </TableHead>
                        <TableHead className="text-right text-[10px]">
                          Deals
                        </TableHead>
                        <TableHead className="text-right text-[10px]">
                          Pipeline $
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.bdr_team.map((b) => (
                        <TableRow key={b.hubspot_owner_id}>
                          <TableCell>{b.display_name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCount(b.deals_week ?? 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCents(b.pipeline_week_cents ?? 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCount(b.deals_month ?? 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCents(b.pipeline_month_cents ?? 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCount(b.deals_quarter ?? 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCents(b.pipeline_quarter_cents ?? 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <NotesArea
              isoYear={year}
              isoWeek={week}
              scope="bdr"
              initialBody={""}
              label="Stan's notes — BDR commentary"
              placeholder="Notes on BDR performance, coverage, leading indicators…"
            />

            {/* Marketing source breakdown */}
            <PipelineBySourceCard rows={data.source_breakdown} />

            {/* Weekly new-companies trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Weekly trend — new companies + SQOs + closed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Week starting</TableHead>
                      <TableHead className="text-right">New companies</TableHead>
                      <TableHead className="text-right">SQOs</TableHead>
                      <TableHead className="text-right">Pipeline added</TableHead>
                      <TableHead className="text-right">Closed-won</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.weekly_new_companies.map((r) => (
                      <TableRow key={r.week_start}>
                        <TableCell className="font-mono text-xs">
                          {fmtDate(r.week_start)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {fmtCount(r.new_companies)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {fmtCount(r.sqo_count)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {fmtCents(r.pipeline_added_cents)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-success">
                          {fmtCents(r.closed_won_cents)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <NotesArea
              isoYear={year}
              isoWeek={week}
              scope="companies_sqos"
              initialBody={""}
              label="Stan's notes — Weekly trend (new companies & SQOs)"
              placeholder="Notes on the weekly trend (what's driving acceleration / deceleration)…"
            />
          </>
        )}
      </div>
    </div>
  );
}

function FunnelRow({ funnel }: { funnel: TofFunnel | null }) {
  if (!funnel) {
    return (
      <p className="text-sm text-muted-foreground">
        No top-of-funnel data for this period.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KpiTile
        label="New companies"
        value={fmtCount(funnel.new_companies)}
      />
      <KpiTile label="SQOs" value={fmtCount(funnel.sqos)} />
      <KpiTile
        label="Paid"
        value={fmtCount(funnel.paid_companies)}
        sub={fmtPct(funnel.paid_conversion_pct, 1)}
      />
      <KpiTile label="Unpaid" value={fmtCount(funnel.unpaid_companies)} />
    </div>
  );
}
